"use client";

import { useEditorStore } from '@/lib/pdf-editor/store';
import { 
  MousePointer, Type, Square, Circle as CircleIcon, 
  Minus, Pencil, Highlighter, Eraser, Image as ImageIcon, 
  StickyNote, Signature, Undo, Redo, Download
} from 'lucide-react';
import { fabric } from 'fabric';
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getPdfEditorCopy } from "@/lib/pdf-editor-copy";

export default function Toolbar() {
  const { activeTool, setActiveTool, fabricCanvases, currentPage } = useEditorStore();
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const editorCopy = getPdfEditorCopy(locale);

  const addShape = (type: 'rect' | 'circle' | 'line') => {
    const canvas = fabricCanvases[currentPage];
    if (!canvas) return;

    let object;
    const center = canvas.getCenter();

    if (type === 'rect') {
      object = new fabric.Rect({
        left: center.left, top: center.top,
        fill: 'transparent', stroke: 'red', strokeWidth: 2,
        width: 100, height: 100
      });
    } else if (type === 'circle') {
      object = new fabric.Circle({
        left: center.left, top: center.top,
        fill: 'transparent', stroke: 'red', strokeWidth: 2,
        radius: 50
      });
    } else if (type === 'line') {
      object = new fabric.Line([50, 50, 200, 50], {
        left: center.left, top: center.top,
        stroke: 'red', strokeWidth: 2
      });
    }

    if (object) {
      canvas.add(object);
      canvas.setActiveObject(object);
      setActiveTool('select');
    }
  };

  const addText = () => {
    const canvas = fabricCanvases[currentPage];
    if (!canvas) return;
    const center = canvas.getCenter();

    const text = new fabric.IText(editorCopy.typeHere, {
      left: center.left, top: center.top,
      fontSize: 20, fill: 'black'
    });
    
    canvas.add(text);
    canvas.setActiveObject(text);
    text.enterEditing();
    text.selectAll();
    setActiveTool('select');
  };

  const tools = [
    { id: 'select', icon: MousePointer, label: editorCopy.select },
    { id: 'text', icon: Type, label: editorCopy.text, action: addText },
    { id: 'rectangle', icon: Square, label: editorCopy.rectangle, action: () => addShape('rect') },
    { id: 'circle', icon: CircleIcon, label: editorCopy.circle, action: () => addShape('circle') },
    { id: 'line', icon: Minus, label: editorCopy.line, action: () => addShape('line') },
    { id: 'draw', icon: Pencil, label: editorCopy.draw },
    { id: 'highlighter', icon: Highlighter, label: editorCopy.highlight },
  ];

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-xl border z-50">
      {tools.map((t) => {
        const Icon = t.icon;
        const isActive = activeTool === t.id;
        
        return (
          <button
            key={t.id}
            onClick={() => {
              if (t.action) t.action();
              else setActiveTool(t.id as any);
            }}
            className={`p-2 rounded-lg transition-all ${
              isActive 
                ? 'bg-blue-500 text-white shadow-md' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            title={t.label}
          >
            <Icon size={20} />
          </button>
        );
      })}
      
      <div className="w-px h-6 bg-gray-200 mx-2" />
      
      <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-700">
        <Undo size={20} />
      </button>
      <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-700">
        <Redo size={20} />
      </button>
      
      <div className="w-px h-6 bg-gray-200 mx-2" />
      
      <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
        <Download size={18} />
        {editorCopy.export}
      </button>
    </div>
  );
}
